import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useSaveSearchFilterMutation } from "../../Queries/FilterQueries";
import { useNotification } from "../../State/Hooks/notificationHooks";
import Modal from "../Modal/Modal";
import { useDebouncedHandler } from "../../State/Hooks/useDebouncedHandler";

interface ISaveSearchFiltersProps {
  queryString: string;
  campaignId?: string | null;
  dialogOpen: boolean;
  clientId?: string;
  handleDialogClose: () => void;
  onSaveCallback?: () => void;
}

export function SaveSearchFilters({
  queryString = "",
  campaignId = null,
  dialogOpen,
  clientId,
  handleDialogClose,
  onSaveCallback,
}: ISaveSearchFiltersProps) {
  const saveSearchMutation = useSaveSearchFilterMutation(clientId);

  const [filterId] = useState<string>("");
  const [filterName, setFilterName] = useState<string>("");
  const [isPersonalFilter, setIsPersonalFilter] = useState(false);
  const [touched, setTouched] = useState<boolean>(false);
  const [isValidFilterName, setIsValidFilterName] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const { displaySuccess, displayError, displayWarning } = useNotification();

  const saveSearchFilters = async () => {
    if (queryString && touched && isValidFilterName) {
      saveSearchMutation.mutate(
        {
          filterName: filterName,
          filterId: filterId || null,
          isPersonalFilter: isPersonalFilter,
          queryString: queryString,
          campaignId: campaignId || null,
        },
        {
          onError: () => {
            displayError("There was an error saving the filter. Please try again!");
          },
          onSuccess: () => {
            displaySuccess("Filter saved!");
            handleDialogClose();
            onSaveCallback && onSaveCallback();
          },
        },
      );
    } else {
      displayWarning("No filters selected, please select at least one filter to save.");
    }
  };

  const handleFilterNameChange = useDebouncedHandler((event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterName(event.target.value);
  }, 300);

  const handleIsPersonalFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsPersonalFilter(event.target.value === "true" ? true : false);
  };

  const resetForm = () => {
    setTimeout(() => {
      setFilterName("");
      setTouched(false);
      setErrorMessage("");
      setIsValidFilterName(true);
    }, 300);
  };

  const handleClose = () => {
    resetForm();
    handleDialogClose();
  };

  useEffect(() => {
    if (filterName.length > 0 && !touched) {
      setTouched(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterName]);

  useEffect(() => {
    if (touched) {
      setIsValidFilterName(filterName.length > 0 && filterName.length <= 50);
      if (filterName.length === 0) {
        setErrorMessage("Filter name is required");
      } else if (filterName.length > 50) {
        setErrorMessage("Filter name is too long (50 characters max)");
      } else {
        setErrorMessage("");
      }
    }
  }, [filterName, touched]);

  return (
    <Modal
      isOpen={dialogOpen}
      handleCloseModal={handleClose}
      title="Save Current Filters"
      dataTestId="save-seach-modal"
      content={
        <>
          <FormControl sx={{ m: 1, width: 300 }}>
            <FormLabel id="filter-name-label" data-test="savefilter-filtername-label">
              Filter Name
            </FormLabel>
            <TextField
              id="filter-name"
              onKeyDown={(e) => e.stopPropagation()}
              data-test="savefilter-filtername"
              onChange={handleFilterNameChange}
              error={touched && !isValidFilterName}
              helperText={errorMessage}
            />
          </FormControl>
          <FormControl sx={{ m: 1, width: 300 }}>
            <FormLabel id="is-personal-filter-label" data-test="savefilter-ispersonalfilter-label">
              Private Filter?
            </FormLabel>
            <RadioGroup
              aria-labelledby="is-personal-filter-label"
              defaultValue="false"
              name="is-personal-filter"
              onChange={handleIsPersonalFilterChange}
            >
              <FormControlLabel value={true} control={<Radio />} label="Yes" />
              <FormControlLabel value={false} control={<Radio />} label="No" />
            </RadioGroup>
          </FormControl>
        </>
      }
      footerContent={
        <Tooltip title={!touched ? "Please enter a filter name" : errorMessage} arrow>
          <Box>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              onClick={saveSearchFilters}
              data-test="savefilter-savefilter-button"
              disabled={!touched || !isValidFilterName}
            >
              Save
            </Button>
          </Box>
        </Tooltip>
      }
    />
  );
}
